import { useEffect } from 'react';

export interface SeoMeta {
  title: string;
  description: string;
  /** Canonical absolute URL — falls back to window.location.origin + path. */
  canonical?: string;
  /** Open Graph image absolute URL. */
  ogImage?: string;
  /** og:type — default 'website'. */
  ogType?: 'website' | 'article';
  /** Locale (e.g. 'en_US' / 'ru_RU'). */
  locale?: string;
  /** og:site_name — default 'Lodestar' on every page. */
  siteName?: string;
  /** ISO date string for article:published_time (og:type='article' only). */
  articlePublished?: string;
  /** ISO date string for article:modified_time (og:type='article' only). */
  articleModified?: string;
  /** article:section — chapter / topic label (og:type='article' only). */
  articleSection?: string;
  /** article:tag — multiple keywords (og:type='article' only). */
  articleTags?: string[];
  /** article:author — author display name (og:type='article' only). */
  articleAuthor?: string;
  /** <meta name="robots"> — default `index,follow,max-image-preview:large`. */
  robots?: string;
}

/**
 * Tiny SSR-free SEO meta setter. We don't pull in react-helmet-async — the
 * landing renders once per route mount and a handful of meta tags is enough.
 *
 * Mutates <head>: <title>, <meta name="description">, og:title/description/
 * image/url/type, twitter:card, <link rel="canonical">.
 *
 * Cleans up on unmount: each managed tag carries data-rm-seo="1" so we can
 * remove on the next route render.
 */
export function useSeoMeta(meta: SeoMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    const upserts: { selector: string; attrs: Record<string, string> }[] = [
      {
        selector: 'meta[name="description"]',
        attrs: { name: 'description', content: meta.description },
      },
      {
        selector: 'meta[property="og:title"]',
        attrs: { property: 'og:title', content: meta.title },
      },
      {
        selector: 'meta[property="og:description"]',
        attrs: { property: 'og:description', content: meta.description },
      },
      {
        selector: 'meta[property="og:type"]',
        attrs: { property: 'og:type', content: meta.ogType ?? 'website' },
      },
      {
        selector: 'meta[name="twitter:card"]',
        attrs: { name: 'twitter:card', content: 'summary_large_image' },
      },
    ];

    const canonical = meta.canonical
      ?? (typeof window !== 'undefined'
        ? `${window.location.origin}${window.location.pathname}`
        : undefined);
    if (canonical) {
      upserts.push({
        selector: 'meta[property="og:url"]',
        attrs: { property: 'og:url', content: canonical },
      });
    }
    if (meta.ogImage) {
      upserts.push({
        selector: 'meta[property="og:image"]',
        attrs: { property: 'og:image', content: meta.ogImage },
      });
    }
    if (meta.locale) {
      upserts.push({
        selector: 'meta[property="og:locale"]',
        attrs: { property: 'og:locale', content: meta.locale },
      });
      // Keep the static og:locale:alternate (index.html ships ru_RU for the
      // EN shell) pointing at the OTHER locale — on RU pages it otherwise
      // duplicates og:locale.
      upserts.push({
        selector: 'meta[property="og:locale:alternate"]',
        attrs: {
          property: 'og:locale:alternate',
          content: meta.locale === 'ru_RU' ? 'en_US' : 'ru_RU',
        },
      });
    }

    // og:site_name — always set; Lodestar by default.
    upserts.push({
      selector: 'meta[property="og:site_name"]',
      attrs: { property: 'og:site_name', content: meta.siteName ?? 'Lodestar' },
    });

    // <meta name="robots"> — defaults to a permissive value that signals
    // Google to render rich previews.  Override per-page if you want
    // noindex (e.g. drafts).
    upserts.push({
      selector: 'meta[name="robots"]',
      attrs: { name: 'robots', content: meta.robots ?? 'index,follow,max-image-preview:large' },
    });

    // og:type='article' extensions — only meaningful for topic articles.
    if (meta.ogType === 'article') {
      if (meta.articlePublished) {
        upserts.push({
          selector: 'meta[property="article:published_time"]',
          attrs: { property: 'article:published_time', content: meta.articlePublished },
        });
      }
      if (meta.articleModified) {
        upserts.push({
          selector: 'meta[property="article:modified_time"]',
          attrs: { property: 'article:modified_time', content: meta.articleModified },
        });
      }
      if (meta.articleSection) {
        upserts.push({
          selector: 'meta[property="article:section"]',
          attrs: { property: 'article:section', content: meta.articleSection },
        });
      }
      if (meta.articleAuthor) {
        upserts.push({
          selector: 'meta[property="article:author"]',
          attrs: { property: 'article:author', content: meta.articleAuthor },
        });
      }
    }

    const owned: Element[] = [];
    for (const { selector, attrs } of upserts) {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('data-rm-seo', '1');
        document.head.appendChild(el);
        owned.push(el);
      }
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    }

    let canonicalEl: HTMLLinkElement | null = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        canonicalEl.setAttribute('data-rm-seo', '1');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    // article:tag — repeated meta property, one per keyword.  Each tag tracked
    // separately so cleanup removes them all on route unmount.
    const tagEls: HTMLMetaElement[] = [];
    if (meta.ogType === 'article' && meta.articleTags?.length) {
      for (const tag of meta.articleTags) {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        el.setAttribute('data-rm-seo-tag', '1');
        document.head.appendChild(el);
        tagEls.push(el);
      }
    }

    return () => {
      document.title = previousTitle;
      for (const el of owned) el.remove();
      for (const el of tagEls) el.remove();
      if (canonicalEl && canonicalEl.getAttribute('data-rm-seo') === '1') {
        canonicalEl.remove();
      }
    };
  }, [
    meta.title,
    meta.description,
    meta.canonical,
    meta.ogImage,
    meta.ogType,
    meta.locale,
    meta.siteName,
    meta.robots,
    meta.articlePublished,
    meta.articleModified,
    meta.articleSection,
    meta.articleAuthor,
    meta.articleTags,
  ]);
}

export interface HreflangAlternate {
  /** BCP-47 code or the literal 'x-default'. */
  hreflang: string;
  /** Absolute URL of the alternate. */
  href: string;
}

/**
 * Injects `<link rel="alternate" hreflang="…">` tags for a bilingual surface
 * that is NOT part of the wiki (the wiki has its own `useWikiHreflang`, which
 * derives the set from `architecture.ts`).
 *
 * Every URL the sitemap advertises with `<xhtml:link rel="alternate">` must
 * emit the reciprocal set in `<head>`, otherwise Google treats the EN and RU
 * twins as duplicates and keeps only one. Cleans up on unmount.
 */
export function useHreflangAlternates(alternates: readonly HreflangAlternate[]): void {
  // Effects can't depend on an array literal (new identity every render), so
  // the pairs are flattened into a primitive key.
  const key = alternates.map((a) => `${a.hreflang}|${a.href}`).join(',');
  useEffect(() => {
    const owned: HTMLLinkElement[] = [];
    for (const pair of key.split(',')) {
      const [hreflang, href] = pair.split('|');
      if (!hreflang || !href) continue;
      const selector = `link[rel="alternate"][hreflang="${hreflang}"][data-rm-seo-alt="1"]`;
      let el = document.head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('data-rm-seo-alt', '1');
        document.head.appendChild(el);
        owned.push(el);
      }
      el.setAttribute('href', href);
    }
    return () => {
      for (const el of owned) el.remove();
    };
  }, [key]);
}

/**
 * Injects a single JSON-LD script tag into <head>. Removes on unmount.
 */
export function useJsonLd(json: string | null, id: string): void {
  useEffect(() => {
    if (!json) return;
    let script = document.head.querySelector<HTMLScriptElement>(
      `script[data-rm-seo-id="${id}"]`,
    );
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-rm-seo-id', id);
      document.head.appendChild(script);
    }
    script.textContent = json;
    return () => {
      script?.remove();
    };
  }, [json, id]);
}
